import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'

// Mirrors the backend rule (mimes + max:10240 KB) so a bad pick is caught before upload.
export const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'doc', 'docx', 'xls', 'xlsx']
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
export const ACCEPTED_HINT = 'PDF, photos (JPG, PNG, HEIC), Word or Excel. Up to 10MB.'

const TYPE_ERROR = "We can't accept that file type. Use a PDF, a photo (JPG, PNG, HEIC), or a Word or Excel file."
const SIZE_ERROR = 'That file is over 10MB. Choose a smaller file or a lower-resolution scan.'

// The backend checks content, not the name, so an accepted MIME type passes even without a matching extension.
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

// Returns why a picked file can't be uploaded, or null when it's fine.
export const fileRejection = (file) => {
  const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : ''
  if (!ACCEPTED_EXTENSIONS.includes(extension) && !ACCEPTED_MIME_TYPES.includes(file.type)) return TYPE_ERROR
  if (file.size > MAX_UPLOAD_BYTES) return SIZE_ERROR
  return null
}

// Laravel's raw mimes/max messages list extensions and kilobytes; show ours instead.
const friendlyServerError = (message) => {
  if (/must be a file of type/i.test(message)) return TYPE_ERROR
  if (/greater than/i.test(message)) return SIZE_ERROR
  return message
}

/**
 * The subscriber's documents (SUB-MVP-53): list, upload, download, plus the
 * documents their agent has requested (SUB-MVP-79) which they fulfil by
 * uploading. Not persisted — re-fetched on every visit. Mirrors ProfileStore's
 * Bearer auth + error handling.
 */
export const useDocumentsStore = defineStore('documentsStore', () => {
  const authStore = useAuthStore()

  const documents = ref([])
  const requests = ref([]) // open document requests from the agent
  const loading = ref(false)
  const loaded = ref(false) // a first successful load has happened
  const loadError = ref(false)
  // Per-control state, keyed by scope ('general' or a request id), so concurrent
  // uploads don't clobber each other's busy/error indication.
  const inFlight = ref(new Set())
  const uploadErrors = ref({})
  const downloadingId = ref(null)
  const downloadError = ref(null)

  const headers = () => ({ Authorization: `Bearer ${authStore.token}` })

  const isUploading = (scope) => inFlight.value.has(scope)

  const handleAuthError = (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      authStore.clearSession()
      if (!import.meta.env.SSR) window.location.href = '/'
      return true
    }
    return false
  }

  // Follow Laravel pagination to the end so older documents and any agent
  // request beyond the first page are never silently dropped. Capped so a
  // pathological account can't loop forever.
  const fetchAll = async (url) => {
    const all = []
    for (let page = 1; page <= 50; page++) {
      const { data } = await axios.get(url, { params: { page }, headers: headers() })
      all.push(...(data.data ?? []))
      const meta = data.meta ?? {}
      if (!meta.last_page || page >= meta.last_page) break
    }
    return all
  }

  // Stale-while-revalidate: only show the blocking spinner on the first load.
  // A revisit keeps the cached documents on screen and refreshes silently.
  const load = async () => {
    const spin = !loaded.value
    if (spin) loading.value = true
    loadError.value = false
    try {
      const [docs, reqs] = await Promise.all([
        fetchAll('/api/client/documents'),
        fetchAll('/api/client/document-requests'),
      ])
      documents.value = docs
      requests.value = reqs
      loaded.value = true
    } catch (error) {
      if (!handleAuthError(error)) loadError.value = true
    } finally {
      if (spin) loading.value = false
    }
  }

  const setUploadError = (scope, message) => {
    uploadErrors.value = { ...uploadErrors.value, [scope]: message }
  }
  const clearUploadError = (scope) => {
    if (scope in uploadErrors.value) {
      const next = { ...uploadErrors.value }
      delete next[scope]
      uploadErrors.value = next
    }
  }

  // Upload a file to the client's document store. When a requestId is given, the
  // new document also fulfils that agent request. `scope` ('general' or the
  // request id) drives per-control busy/error state.
  const upload = async (file, { requestId = null, scope = 'general' } = {}) => {
    inFlight.value.add(scope)
    clearUploadError(scope)
    try {
      const { data } = await axios.post('/api/client/documents', file2form(file), {
        headers: { ...headers(), Accept: 'application/json' },
      })
      const doc = data.data ?? data
      documents.value = [doc, ...documents.value]
      // If this upload answers a request, the whole action only succeeds when
      // the fulfil lands too — otherwise a retry would upload the file again.
      if (requestId) return await fulfil(requestId, doc.id, scope)
      return true
    } catch (error) {
      if (handleAuthError(error)) return false
      const serverMessage = Object.values(error?.response?.data?.errors ?? {})[0]?.[0]
      const message =
        error?.response?.status === 422
          ? serverMessage
            ? friendlyServerError(serverMessage)
            : 'That file was rejected. Use a PDF, image or Office file up to 10MB.'
          : 'Upload failed. Please try again.'
      setUploadError(scope, message)
      return false
    } finally {
      inFlight.value.delete(scope)
    }
  }

  const fulfil = async (requestId, clientDocumentId, scope = requestId) => {
    try {
      await axios.post(
        `/api/client/document-requests/${requestId}/fulfil`,
        { client_document_id: clientDocumentId },
        { headers: headers() }
      )
      // The request is answered — drop it from the open list.
      requests.value = requests.value.filter((r) => r.id !== requestId)
      return true
    } catch (error) {
      if (handleAuthError(error)) return false
      setUploadError(scope, "Your file uploaded, but we couldn't attach it to the request. Please try again.")
      return false
    }
  }

  // /client/* needs the bearer, so a plain <a download> can't authenticate.
  // Fetch the file as a blob with the header, then hand it to the browser.
  const download = async (doc) => {
    downloadingId.value = doc.id
    downloadError.value = null
    try {
      const { data } = await axios.get(`/api/client/documents/${doc.id}/download`, {
        responseType: 'blob',
        headers: headers(),
      })
      const url = URL.createObjectURL(data)
      const link = window.document.createElement('a')
      link.href = url
      link.download = doc.file_name || 'document'
      window.document.body.appendChild(link)
      link.click()
      link.remove()
      // Defer the revoke: some browsers read the blob asynchronously after
      // click(), and revoking in the same tick can cancel/truncate the download.
      setTimeout(() => URL.revokeObjectURL(url), 10000)
    } catch (error) {
      if (!handleAuthError(error)) downloadError.value = doc.id
    } finally {
      downloadingId.value = null
    }
  }

  const reset = () => {
    documents.value = []
    requests.value = []
    loading.value = false
    loaded.value = false
    loadError.value = false
    inFlight.value = new Set()
    uploadErrors.value = {}
    downloadingId.value = null
    downloadError.value = null
  }

  return {
    documents,
    requests,
    loading,
    loadError,
    uploadErrors,
    downloadingId,
    downloadError,
    isUploading,
    setUploadError,
    load,
    upload,
    fulfil,
    download,
    reset,
  }
})

// Build the multipart body for a single-file upload.
function file2form(file) {
  const form = new FormData()
  form.append('file', file)
  return form
}
